import { Order, Product } from "../../types/interfaces";
import { WorkflowType, ItemMaterials } from "../../types/enums";

export type ProsthesisMappedValues = Record<string, unknown> & {
  material0: string;
  shade0: string;
  gingivaShade0: string;
  material1?: string;
  shade1?: string;
  gingivaShade1?: string;
};

interface RemovableItemUpdate {
  data: {
    itemMaterial: { connect: { id: string } };
    teethShade?: {
      connect?: { id: string };
      disconnect?: { id: { equals: string } };
    };
    gingivaShade?: {
      connect?: { id: string };
      disconnect?: { id: { equals: string } };
    };
    partialDenture?: {
      update: {
        data: {
          lingualMetalBand: { set: boolean };
          metalMesh: { set: boolean };
          palatalMetalBand: { set: boolean };
          customTray: { set: boolean };
          hooks: { deleteMany: { partialDentureId: { equals: string } } };
        };
        where: {
          removableItemId: { equals: string };
        };
      };
    };
  };
  where: { id: string };
}

const updateProsthesisGraphql = (
  mappedValues: ProsthesisMappedValues,
  product: Product
): RemovableItemUpdate[] => {
  const values: RemovableItemUpdate[] = [];
  const removableItem = [...product.removableItem];
  removableItem
    .sort((a, b) => (a.teeth?.[0] ?? 0) - (b.teeth?.[0] ?? 0))
    .forEach((item, index) => {
      if (
        item.workflowType !== WorkflowType.WAX_RIM &&
        mappedValues[`material${index}`] &&
        mappedValues[`shade${index}`] &&
        mappedValues[`gingivaShade${index}`]
      ) {
        let data = {
          data: {
            itemMaterial: {
              connect: {
                id: mappedValues[`material${index}`] as string,
              },
            },
            teethShade: {
              connect: {
                id: mappedValues[`shade${index}`] as string,
              },
            },
            gingivaShade: {
              connect: {
                id: mappedValues[`gingivaShade${index}`] as string,
              },
            },
            partialDenture: undefined,
          },
          where: {
            id: item.id,
          },
        };
        if (
          mappedValues[`material${index}`] === ItemMaterials.VALPLAST &&
          item.partialDenture
        ) {
          data.data.partialDenture = {
            update: {
              data: {
                lingualMetalBand: {
                  set: false,
                },
                metalMesh: {
                  set: false,
                },
                palatalMetalBand: {
                  set: false,
                },
                customTray: {
                  set: false,
                },
                hooks: {
                  deleteMany: [
                    {
                      partialDentureId: {
                        equals: item.partialDenture?.id,
                      },
                    },
                  ],
                },
              },
              where: {
                id: {
                  equals: item.partialDenture?.id,
                },
              },
            },
          };
        }
        values.push(data);
      } else if (
        item.workflowType === WorkflowType.WAX_RIM &&
        mappedValues[`material${index}`]
      ) {
        values.push({
          data: {
            itemMaterial: {
              connect: {
                id: mappedValues[`material${index}`] as string,
              },
            },
            teethShade: item.teethShade?.id
              ? {
                  disconnect: {
                    id: {
                      equals: item.teethShade?.id,
                    },
                  },
                }
              : undefined,
            gingivaShade: item.gingivaShade?.id
              ? {
                  disconnect: {
                    id: {
                      equals: item.gingivaShade?.id,
                    },
                  },
                }
              : undefined,
          },
          where: {
            id: item.id,
          },
        });
      }
    });
  return values;
};
export const updateProsthesis = async (
  updateOrder: (data: {
    variables: {
      data: any;
      where: { id: string };
    };
  }) => Promise<Order>,
  mappedValues: ProsthesisMappedValues,
  orderId: string,
  product: Product
) => {
  try {
    if (
      !product.removableItem ||
      !Array.isArray(product.removableItem) ||
      product.removableItem.length === 0
    ) {
      throw new Error("No removable items found in product");
    }
    return await updateOrder({
      variables: {
        data: {
          products: {
            update: {
              data: {
                removableItem: {
                  update: updateProsthesisGraphql(mappedValues, product),
                },
              },
              where: {
                id: product.id,
              },
            },
          },
        },
        where: {
          id: orderId,
        },
      },
    });
  } catch (error) {
    console.error("Failed to update prosthesis:", error);
    throw error;
  }
};
